import { Logger } from '@/logger'
import { UVO_USERINFO_SCHEMA } from '@bldsoft/stpanalytix'
import type { AnalytixSendMethods } from './index.h'

/**
 * Debug logger for Analytix adapter traffic.
 */
export const logger = new Logger('uvo/analytix')

/**
 * Adapter method name → backend event_type (matches STPUVOAnalytix / listener contract).
 */
export const ANALYTIX_EVENT_NAMES = {
  sendPageOpenEvent: 'page_open',
  sendFavoritesAddEvent: 'favorite_add',
  sendFavoritesRemoveEvent: 'favorite_remove',
  sendSessionStartEvent: 'session_start',
  sendSearchEvent: 'search',
  sendPlayerOpenEvent: 'player_open',
  sendPlayStartEvent: 'play_start',
  sendPlayStopEvent: 'play_stop',
  sendPlayerWatchingEvent: 'player_watching',
  sendPlayerEvent: 'player_event',
  sendPlayErrorEvent: 'play_error',
  sendEpgViewEvent: 'epg_view',
  sendEpgCategoryEvent: 'epg_category',
  sendEpgChannelEvent: 'epg_channel',
  sendEpgProgramEvent: 'epg_program',
  sendBannerImpressionEvent: 'banner_impression',
  sendBannerClickEvent: 'banner_click',
  sendBannerDismissEvent: 'banner_dismiss',
  sendLiveModuleViewEvent: 'live_module_view',
  sendSignUpEvent: 'sign_up',
} as const satisfies Record<keyof AnalytixSendMethods, string>

/**
 * Maps user-info object keys to listener API field names.
 */
export const ANALYTIX_GENERAL_PARAM_NAMES = (
  UVO_USERINFO_SCHEMA as ReadonlyArray<{ name: string; apiField: string }>
).reduce(
  (acc: Record<string, string>, curr) => {
    acc[curr.name] = curr.apiField
    return acc
  },
  {} as Record<string, string>
)

/**
 * Payload field → event_parameter slot per event (aligned with @bldsoft/stpanalytix UVO schemas).
 */
export const ANALYTIX_EVENT_PARAM_NAMES = {
  session_start: {
    message: 'event_parameter7',
  },
  page_open: {
    pagePath: 'event_parameter2',
    entityId: 'event_parameter3',
    pageTitle: 'event_parameter4',
    creatorId: 'event_parameter8',
    utmSource: 'event_parameter11',
    utmMedium: 'event_parameter12',
    utmCampaign: 'event_parameter13',
    utmTerm: 'event_parameter14',
    utmContent: 'event_parameter15',
  },
  favorite_add: {
    itemId: 'event_parameter1',
    itemType: 'event_parameter2',
    title: 'event_parameter4',
    creatorId: 'event_parameter8',
  },
  favorite_remove: {
    itemId: 'event_parameter1',
    itemType: 'event_parameter2',
    title: 'event_parameter4',
    creatorId: 'event_parameter8',
  },
  search: {
    query: 'event_parameter1',
    searchType: 'event_parameter2',
  },
  player_open: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentUrl: 'event_parameter3',
    contentTitle: 'event_parameter4',
    openFrom: 'event_parameter6',
    errorCode: 'event_parameter7',
    creatorId: 'event_parameter8',
    playerSessionId: 'event_parameter10',
    region: 'event_parameter15',
    fullscreenType: 'event_parameter18',
  },
  play_start: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentUrl: 'event_parameter3',
    audioLanguage: 'event_parameter4',
    subtitlesLanguage: 'event_parameter5',
    startSecond: 'event_parameter6',
    contentLength: 'event_parameter7',
    creatorId: 'event_parameter8',
    playerSessionId: 'event_parameter10',
    category: 'event_parameter11',
    programOrEpisodeId: 'event_parameter12',
    programOrEpisodeTitle: 'event_parameter13',
    playbackTrigger: 'event_parameter14',
    region: 'event_parameter15',
    watchId: 'event_parameter17',
    fullscreenType: 'event_parameter18',
    cmcdSessionId: 'event_parameter20',
  },
  play_stop: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentUrl: 'event_parameter3',
    audioLanguage: 'event_parameter4',
    subtitlesLanguage: 'event_parameter5',
    stopSecond: 'event_parameter6',
    contentLength: 'event_parameter7',
    creatorId: 'event_parameter8',
    playerSessionId: 'event_parameter10',
    programOrEpisodeId: 'event_parameter12',
    programOrEpisodeTitle: 'event_parameter13',
    region: 'event_parameter15',
    watchId: 'event_parameter17',
    fullscreenType: 'event_parameter18',
    cmcdSessionId: 'event_parameter20',
  },
  player_watching: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentUrl: 'event_parameter3',
    audioLanguage: 'event_parameter4',
    subtitlesLanguage: 'event_parameter5',
    playerSecond: 'event_parameter6',
    contentLength: 'event_parameter7',
    creatorId: 'event_parameter8',
    playerSessionId: 'event_parameter10',
    programOrEpisodeId: 'event_parameter12',
    programOrEpisodeTitle: 'event_parameter13',
    region: 'event_parameter15',
    watchId: 'event_parameter17',
    fullscreenType: 'event_parameter18',
    cmcdSessionId: 'event_parameter20',
  },
  player_event: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentUrl: 'event_parameter3',
    audioLanguage: 'event_parameter4',
    subtitlesLanguage: 'event_parameter5',
    playerSecond: 'event_parameter6',
    contentLength: 'event_parameter7',
    creatorId: 'event_parameter8',
    playerSessionId: 'event_parameter10',
    playerEventType: 'event_parameter11',
    programOrEpisodeId: 'event_parameter12',
    programOrEpisodeTitle: 'event_parameter13',
    region: 'event_parameter15',
    watchId: 'event_parameter17',
    fullscreenType: 'event_parameter18',
    cmcdSessionId: 'event_parameter20',
  },
  play_error: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentUrl: 'event_parameter3',
    errorCode: 'event_parameter4',
    UIErrorCode: 'event_parameter5',
    errorMessage: 'event_parameter6',
    creatorId: 'event_parameter8',
    playerSessionId: 'event_parameter10',
    region: 'event_parameter15',
    watchId: 'event_parameter17',
    fullscreenType: 'event_parameter18',
    cmcdSessionId: 'event_parameter20',
  },
  epg_view: {
    channelId: 'event_parameter1',
    channelTitle: 'event_parameter2',
    tabId: 'event_parameter3',
  },
  epg_category: {
    categoryId: 'event_parameter4',
    categoryTitle: 'event_parameter5',
  },
  epg_channel: {
    channelId: 'event_parameter1',
    channelTitle: 'event_parameter2',
    categoryId: 'event_parameter4',
    categoryTitle: 'event_parameter5',
    channelOrder: 'event_parameter6',
  },
  epg_program: {
    channelId: 'event_parameter1',
    channelTitle: 'event_parameter2',
    categoryId: 'event_parameter4',
    categoryTitle: 'event_parameter5',
    channelOrder: 'event_parameter6',
    programTitle: 'event_parameter7',
    programStart: 'event_parameter8',
    programStatus: 'event_parameter10',
    programEnd: 'event_parameter11',
  },
  live_module_view: {
    previousView: 'event_parameter1',
    currentView: 'event_parameter2',
    regionTitle: 'event_parameter3',
  },
  banner_impression: {
    pageTitle: 'event_parameter1',
    triggerType: 'event_parameter2',
    bannerType: 'event_parameter3',
    playerSessionId: 'event_parameter10',
  },
  banner_click: {
    pageTitle: 'event_parameter1',
    triggerType: 'event_parameter2',
    bannerType: 'event_parameter3',
    redirectDestination: 'event_parameter4',
    playerSessionId: 'event_parameter10',
  },
  banner_dismiss: {
    pageTitle: 'event_parameter1',
    triggerType: 'event_parameter2',
    bannerType: 'event_parameter3',
    playerSessionId: 'event_parameter10',
  },
  sign_up: {
    signUpMethod: 'event_parameter1',
    entryPoint: 'event_parameter2',
  },
} as const

type AnalytixDebugParams = Record<string, unknown>

export const debugAnalytixEvent = ({
  eventName,
  params = {},
  general = {},
  emoji,
}: {
  eventName: string
  params?: AnalytixDebugParams
  general?: AnalytixDebugParams
  emoji?: string
}) => {
  const title = emoji ? `${emoji} ${eventName}` : eventName
  logger.groupCollapsed(title)
  const table: Record<string, { parameter: string; value: unknown }> = {
    eventName: { parameter: 'event_name', value: eventName },
  }

  for (const [key, value] of Object.entries(general)) {
    table[key] = {
      parameter: ANALYTIX_GENERAL_PARAM_NAMES[key] ?? key,
      value,
    }
  }

  const eventParamMap =
    (ANALYTIX_EVENT_PARAM_NAMES as Record<string, Record<string, string>>)[
      eventName
    ] ?? {}

  for (const [key, value] of Object.entries(params)) {
    if (key.startsWith('(')) continue
    table[key] = {
      parameter: eventParamMap[key] ?? key,
      value,
    }
  }

  // eslint-disable-next-line no-console
  console.table(table)
  logger.groupEnd()
}
